import { useEffect, useState, type ReactNode } from "react";
import {
  Button,
  SubmitButton,
  SubmittingHint,
  useSubmittingMutation,
} from "@/design-system/components/ui";
import { dealSubmissionApi } from "../../api/dealSubmission";
import { PortalApiError } from "../../api/client";
import type {
  DealSubmissionAiFeedbackResult,
  DealSubmissionDocumentDto,
  DealSubmissionDocumentRequestDto,
  DealSubmissionScanStatus,
  DealSubmissionSectionDto,
  DealSubmissionSectionStateDto,
} from "../../api/types";
import {
  normalizeFeedback,
  parseSectionFeedback,
  SectionFeedbackPanel,
} from "./SectionFeedbackPanel";
import {
  evaluationStatusLabel,
  evaluationStatusTone,
  formatOutstandingEvaluationMessage,
  formatSectionPrerequisiteMessage,
  outstandingDocumentEvaluationNames,
  outstandingEvaluationNames,
} from "./evaluationFreshness";
import {
  documentEvalKey,
  passthroughEvaluationTracker,
  sectionEvalKey,
  type EvaluationTracker,
} from "./evaluationTracker";

// Wizard documents page — one upload widget per DealSubmissionDocumentRequest.
// Each upload creates/replaces exactly one DealSubmissionDocument keyed
// to that request (replace-on-reupload). Scan status is shown for the
// broker's awareness but does NOT gate submission (filing gates on
// Clean later, in staging).

interface DocumentsSectionProps {
  submissionId: string;
  section: DealSubmissionSectionDto;
  sectionState: DealSubmissionSectionStateDto | null;
  documentRequests: DealSubmissionDocumentRequestDto[];
  documents: DealSubmissionDocumentDto[];
  editable: boolean;
  // Runner-owned in-flight evaluation tracker — survives section navigation.
  // Optional: defaults to an inline passthrough when rendered outside the runner.
  evaluation?: EvaluationTracker;
  commentPanel?: ReactNode;
  onChanged: () => Promise<void>;
  onContinue: () => Promise<void>;
  onExit: () => void;
}

export function DocumentsSection({
  submissionId,
  section,
  sectionState,
  documentRequests,
  documents,
  editable,
  evaluation = passthroughEvaluationTracker,
  commentPanel,
  onChanged,
  onContinue,
  onExit,
}: DocumentsSectionProps) {
  const ordered = [...documentRequests].sort((a, b) => a.order - b.order);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const evalKey = sectionEvalKey(section.id);
  // Prefer a result the tracker retained for this section (shows immediately on
  // return, even before the persisted reload lands); else the persisted scan.
  const trackerResult = evaluation.result(evalKey);
  const [feedback, setFeedback] = useState<DealSubmissionAiFeedbackResult | null>(
    () => trackerResult ?? parseSectionFeedback(sectionState?.aiFeedbackJson ?? null),
  );
  useEffect(() => {
    setFeedback(trackerResult ?? parseSectionFeedback(sectionState?.aiFeedbackJson ?? null));
  }, [trackerResult, sectionState?.aiFeedbackJson]);
  // The tracker de-dupes (a second click while running is a no-op) and
  // reloads the submission on completion so the persisted feedback renders
  // even if the broker navigated away mid-run.
  const evaluate = useSubmittingMutation(async () => {
    await evaluation.run(evalKey, async () => {
      try {
        if (!sectionState) {
          await dealSubmissionApi.saveSection(submissionId, section.id, {
            savedDataJson: null,
            status: "InProgress",
          });
        }
        const result = await dealSubmissionApi.evaluateSection(submissionId, section.id);
        const fb = normalizeFeedback(result);
        setFeedback(fb);
        return fb;
      } catch (err) {
        const msg =
          err instanceof PortalApiError ? err.message
            : err instanceof Error ? err.message
            : "Couldn't evaluate this section.";
        setErrorMsg(msg);
        return null;
      }
    });
  });
  const evaluating = evaluate.pending || evaluation.isEvaluating(evalKey);
  const saveAndContinue = useSubmittingMutation(
    async () => {
      await dealSubmissionApi.saveSection(submissionId, section.id, {
        savedDataJson: null,
        status: "Complete",
      });
      await onContinue();
    },
    {
      onError: (err) => {
        const msg =
          err instanceof PortalApiError ? err.message
            : err instanceof Error ? err.message
            : "Couldn't save this section.";
        setErrorMsg(msg);
      },
    },
  );
  const saving = saveAndContinue.pending;
  const requiredMissing = ordered
    .filter((r) => r.required)
    .filter((r) => !documents.some((d) => d.documentRequestId === r.id));
  const outstandingEvaluations = outstandingEvaluationNames(
    section,
    sectionState,
    documentRequests,
    documents,
  );
  const evaluationMessage = formatOutstandingEvaluationMessage(outstandingEvaluations);
  // Section feedback rolls up the per-document reviews, so it can't run until
  // every document in this section is evaluated. Gate the button (the server
  // rejects it too) and explain why.
  const sectionDocPrereqs = outstandingDocumentEvaluationNames(documentRequests, documents);
  const sectionPrereqMessage = formatSectionPrerequisiteMessage(sectionDocPrereqs);
  const sectionEvaluationStatus = section.feedbackEnabled
    ? sectionState?.aiFeedbackStatus ?? "NotEvaluated"
    : "NotApplicable";
  const needsCompleteSave = requiredMissing.length === 0
    && outstandingEvaluations.length === 0
    && sectionState?.status !== "Complete";

  function handleEvaluateSection() {
    setErrorMsg(null);
    void evaluate.submit();
  }

  function handleContinue() {
    setErrorMsg(null);
    if (requiredMissing.length > 0) {
      setErrorMsg(`${requiredMissing.length} required document${requiredMissing.length === 1 ? "" : "s"} still missing.`);
      return;
    }
    if (evaluationMessage) {
      setErrorMsg(evaluationMessage);
      return;
    }
    void saveAndContinue.submit();
  }

  return (
    <div className="flex flex-col gap-space-4" data-testid={`section-docs-${section.order}`}>
      <header>
        <h2 className="text-h3 font-display text-onyx m-0">{section.title}</h2>
        {section.instructions && (
          <p className="text-small text-gray-mid mt-space-2 whitespace-pre-line">
            {section.instructions}
          </p>
        )}
      </header>

      {commentPanel}

      <DocumentRequestList
        submissionId={submissionId}
        requests={documentRequests}
        documents={documents}
        editable={editable}
        evaluation={evaluation}
        onChanged={onChanged}
        emptyMessage="No documents requested in this section."
      />

      {editable && section.feedbackEnabled && (
        <div>
          <div
            className={`text-caption font-medium mb-space-2 ${evaluationStatusTone(sectionEvaluationStatus)}`}
            data-testid="section-evaluation-status"
          >
            {evaluationStatusLabel(sectionEvaluationStatus)}
          </div>
          <SubmitButton
            variant="secondary"
            onClick={handleEvaluateSection}
            pending={evaluating}
            pendingLabel="Evaluating"
            disabled={saving || sectionDocPrereqs.length > 0 || requiredMissing.length > 0}
            data-testid="section-evaluate"
          >
            Evaluate &amp; Feedback
          </SubmitButton>
          {(requiredMissing.length > 0 || sectionPrereqMessage) && (
            <p
              className="text-caption text-status-error m-0 mt-space-2"
              role="status"
              data-testid="section-evaluate-blocked"
            >
              {requiredMissing.length > 0
                ? `Upload the required document${requiredMissing.length === 1 ? "" : "s"} before evaluating.`
                : sectionPrereqMessage}
            </p>
          )}
          {evaluating && (
            <p className="text-caption text-gray-mid m-0 mt-space-2" role="status">
              This can take a few minutes — you can keep working on other sections while it runs.
            </p>
          )}
        </div>
      )}

      {feedback && <SectionFeedbackPanel feedback={feedback} context="section" />}

      {evaluationMessage && (
        <p className="text-small text-status-error m-0" role="alert" data-testid="evaluation-gate-message">
          {evaluationMessage}
        </p>
      )}

      {needsCompleteSave && (
        <p className="text-small text-status-warning m-0" role="status" data-testid="section-complete-needed-message">
          Click Save &amp; Continue to mark {section.title} complete before submitting.
        </p>
      )}

      {errorMsg && <p className="text-small text-status-error m-0" role="alert">{errorMsg}</p>}

      {editable && (
        <div className="sticky bottom-0 z-10 -mx-space-1 flex items-center justify-between gap-space-3 flex-wrap border-t border-border-light bg-white-warm/95 px-space-1 py-space-3 backdrop-blur-sm">
          <span className="text-caption text-gray-mid">
            {requiredMissing.length > 0
              ? `${requiredMissing.length} required document${requiredMissing.length === 1 ? "" : "s"} still missing.`
              : "All required documents uploaded."}
          </span>
          <div className="flex items-center gap-space-2 flex-wrap">
            <Button
              variant="secondary"
              onClick={onExit}
              disabled={saving}
              data-testid="wizard-save-and-exit"
            >
              Save &amp; exit
            </Button>
            <SubmitButton
              onClick={handleContinue}
              pending={saving}
              pendingLabel="Saving…"
              disabled={requiredMissing.length > 0 || outstandingEvaluations.length > 0}
              data-testid="wizard-save-and-continue"
            >
              Save &amp; continue
            </SubmitButton>
          </div>
          <SubmittingHint pending={saving} className="w-full" />
        </div>
      )}
    </div>
  );
}

// RECON→FIX — a section's document uploaders, rendered 1:1 from the
// template's document requests for THAT section. Extracted so any section
// (DataEntry or Documents) can render the documents the admin authored
// under it: previously only the §8 "Documents"-kind section rendered
// uploaders, so document requests attached to DataEntry sections (e.g.
// "P&L (3 years)" on Financial Information) had no upload control yet
// still gated submit — a dead end. Now every request is visible and
// uploadable in the section it was authored under.
interface DocumentRequestListProps {
  submissionId: string;
  requests: DealSubmissionDocumentRequestDto[];
  documents: DealSubmissionDocumentDto[];
  editable: boolean;
  // Optional: defaults to an inline passthrough when rendered outside the runner.
  evaluation?: EvaluationTracker;
  onChanged: () => Promise<void>;
  emptyMessage: string;
}

export function DocumentRequestList({
  submissionId,
  requests,
  documents,
  editable,
  evaluation = passthroughEvaluationTracker,
  onChanged,
  emptyMessage,
}: DocumentRequestListProps) {
  const ordered = [...requests].sort((a, b) => a.order - b.order);
  if (ordered.length === 0) {
    return <p className="text-small text-gray-mid m-0">{emptyMessage}</p>;
  }
  return (
    <ul className="list-none p-0 m-0 flex flex-col gap-space-3">
      {ordered.map((req) => (
        <li key={req.id}>
          <DocumentRequestUploader
            submissionId={submissionId}
            request={req}
            document={documents.find((d) => d.documentRequestId === req.id) ?? null}
            editable={editable}
            evaluation={evaluation}
            onChanged={onChanged}
          />
        </li>
      ))}
    </ul>
  );
}

interface DocumentRequestUploaderProps {
  submissionId: string;
  request: DealSubmissionDocumentRequestDto;
  document: DealSubmissionDocumentDto | null;
  editable: boolean;
  evaluation: EvaluationTracker;
  onChanged: () => Promise<void>;
}

function DocumentRequestUploader({
  submissionId,
  request,
  document: existing,
  editable,
  evaluation,
  onChanged,
}: DocumentRequestUploaderProps) {
  const [error, setError] = useState<string | null>(null);
  const evalKey = existing ? documentEvalKey(existing.id) : null;
  // Advisory per-document AI feedback — prefer a result the tracker retained
  // for this document (shows immediately on return); else any prior persisted
  // scan. Set optimistically on a successful run too. The verdict never gates.
  const trackerResult = evalKey ? evaluation.result(evalKey) : null;
  const [feedback, setFeedback] = useState<DealSubmissionAiFeedbackResult | null>(
    () => trackerResult ?? currentDocumentFeedback(existing),
  );
  const accept = buildAcceptAttribute(request.allowedTypes);

  useEffect(() => {
    setFeedback(trackerResult ?? currentDocumentFeedback(existing));
  }, [trackerResult, existing?.id, existing?.aiFeedbackJson, existing?.aiFeedbackStatus]);

  const upload = useSubmittingMutation(
    async (file: File) => {
      await dealSubmissionApi.uploadDocument(submissionId, request.id, file);
    },
    {
      refresh: onChanged,
      onError: (err) => {
        const msg =
          err instanceof PortalApiError ? err.message
            : err instanceof Error ? err.message
            : "Upload failed. Try again or pick a smaller file.";
        setError(msg);
      },
    },
  );
  const busy = upload.pending;

  // The tracker de-dupes (a second click while running is a no-op), retains
  // the result so it renders on return, and reloads in the background.
  const evaluate = useSubmittingMutation(async () => {
    if (!existing || !evalKey) return;
    await evaluation.run(evalKey, async () => {
      try {
        const result = await dealSubmissionApi.evaluateDocument(submissionId, existing.id);
        const fb = normalizeFeedback(result);
        setFeedback(fb);
        return fb;
      } catch (err) {
        const msg =
          err instanceof PortalApiError ? err.message
            : err instanceof Error ? err.message
            : "Couldn't evaluate this document.";
        setError(msg);
        return null;
      }
    });
  });
  const evaluating = evaluate.pending || (evalKey ? evaluation.isEvaluating(evalKey) : false);

  function handleUpload(file: File) {
    setError(null);
    void upload.submit(file);
  }

  function handleEvaluate() {
    if (!existing || !evalKey) return;
    setError(null);
    void evaluate.submit();
  }

  return (
    <div
      className="p-space-3 border border-border-light rounded-md bg-white-pure"
      data-testid={`doc-request-${request.key}`}
    >
      <div className="flex items-start justify-between gap-space-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-small font-medium text-onyx m-0">
            {request.label}{" "}
            {request.required && (
              <span className="text-caption text-status-error">required</span>
            )}
          </p>
          {request.instructions && (
            <p className="text-caption text-gray-mid m-0 mt-space-1 whitespace-pre-line">
              {request.instructions}
            </p>
          )}
          {request.allowedTypes && (
            <p className="text-caption text-gray-mid m-0 mt-space-1">
              Allowed: {request.allowedTypes}
            </p>
          )}
        </div>
        <StatusPill document={existing} />
      </div>

      {existing && (
        <p
          className="text-caption text-gray-mid m-0 mt-space-2"
          data-testid={`doc-existing-${request.key}`}
        >
          Uploaded: <strong>{existing.originalFileName}</strong>
          {" · "}
          {(existing.sizeBytes / 1024).toFixed(0)} KB
        </p>
      )}

      {editable && (
        <label className="block mt-space-3">
          <span className="block text-caption text-gray-mid mb-space-1">
            {existing ? "Replace this file" : "Choose a file"}
          </span>
          <input
            type="file"
            accept={accept}
            disabled={busy}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleUpload(f);
              // Reset so re-selecting the same file fires onChange again.
              e.target.value = "";
            }}
            className="block w-full text-small"
            data-testid={`doc-input-${request.key}`}
          />
        </label>
      )}

      <SubmittingHint pending={busy} message="Uploading…" className="mt-space-2" />
      {error && (
        <p
          className="text-small text-status-error m-0 mt-space-2"
          role="alert"
        >
          {error}
        </p>
      )}

      {existing && request.feedbackEnabled && (
        <div
          className={`mt-space-3 text-caption font-medium ${evaluationStatusTone(existing.aiFeedbackStatus)}`}
          data-testid={`doc-evaluation-status-${request.key}`}
        >
          {evaluationStatusLabel(existing.aiFeedbackStatus)}
        </div>
      )}

      {editable && existing && request.feedbackEnabled && (
        <div className="mt-space-3">
          <SubmitButton
            variant="secondary"
            size="sm"
            onClick={handleEvaluate}
            pending={evaluating}
            pendingLabel="Evaluating"
            disabled={busy}
            data-testid={`doc-evaluate-${request.key}`}
          >
            Evaluate &amp; Feedback
          </SubmitButton>
          {evaluating && (
            <p className="text-caption text-gray-mid m-0 mt-space-2" role="status">
              This can take a few minutes — you can keep working on other sections while it runs.
            </p>
          )}
        </div>
      )}

      {feedback && (
        <div className="mt-space-3">
          <SectionFeedbackPanel feedback={feedback} context="document" />
        </div>
      )}
    </div>
  );
}


function currentDocumentFeedback(
  document: DealSubmissionDocumentDto | null,
): DealSubmissionAiFeedbackResult | null {
  return document?.aiFeedbackStatus === "Current"
    ? parseSectionFeedback(document.aiFeedbackJson ?? null)
    : null;
}

function StatusPill({ document }: { document: DealSubmissionDocumentDto | null }) {
  if (!document) {
    return (
      <span className="text-caption text-gray-mid shrink-0" data-testid="doc-status-empty">
        Not uploaded
      </span>
    );
  }
  return (
    <span
      className={`text-caption shrink-0 ${toneFor(document.scanStatus)}`}
      data-testid={`doc-status-${document.scanStatus.toLowerCase()}`}
    >
      {scanLabel(document.scanStatus)}
    </span>
  );
}

function scanLabel(scan: DealSubmissionScanStatus): string {
  switch (scan) {
    case "Pending": return "Uploaded · scanning";
    case "Clean": return "Uploaded · clean";
    case "Infected": return "Rejected · infected";
    case "Error": return "Scan error";
  }
}

function toneFor(scan: DealSubmissionScanStatus): string {
  switch (scan) {
    case "Clean": return "text-status-success";
    case "Infected":
    case "Error": return "text-status-error";
    case "Pending": return "text-gray-mid";
  }
}

function buildAcceptAttribute(allowedTypes: string | null): string | undefined {
  if (!allowedTypes) return undefined;
  return allowedTypes
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean)
    .map((t) => (t.startsWith(".") ? t : `.${t}`))
    .join(",");
}
